import { useState, useEffect } from 'react';

type ConfirmationType = 'profile' | 'payroll';

/**
 * Generate storage key for data accuracy confirmation
 */
const getStorageKey = (type: ConfirmationType, extraId?: number) => {
  const baseKey = `${type}_confirmation_checkbox`;
  return extraId ? `${baseKey}_${extraId}` : baseKey;
};

/**
 * Custom hook for managing data accuracy confirmation state with localStorage persistence
 * 
 * @param type - Type of confirmation ('profile' or 'payroll')
 * @param extraId - Optional extra ID for user-specific storage
 * @returns Object with confirmation state and management functions
 */
export const useDataAccuracyConfirmation = (type: ConfirmationType, extraId?: number) => {
  const [acknowledgesDataAccuracy, setAcknowledgesDataAccuracy] = useState(false);
  
  const storageKey = getStorageKey(type, extraId);
  
  useEffect(() => {
    // Load from localStorage on component mount
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved === 'true') {
        setAcknowledgesDataAccuracy(true);
      }
    } catch (error) {
      console.warn('Failed to load confirmation state from localStorage:', error);
    }
  }, [storageKey]);

  /**
   * Update confirmation state and persist to localStorage
   */
  const setConfirmation = (value: boolean) => {
    setAcknowledgesDataAccuracy(value);
    
    try {
      if (value) {
        localStorage.setItem(storageKey, 'true');
      } else {
        localStorage.removeItem(storageKey);
      }
    } catch (error) {
      console.warn('Failed to save confirmation state to localStorage:', error);
    }
  };

  /**
   * Clear confirmation state from both memory and localStorage
   */
  const clearConfirmation = () => {
    try {
      localStorage.removeItem(storageKey);
      setAcknowledgesDataAccuracy(false);
    } catch (error) {
      console.warn('Failed to clear confirmation state from localStorage:', error);
    }
  };

  return {
    acknowledgesDataAccuracy,
    setAcknowledgesDataAccuracy: setConfirmation,
    clearConfirmation,
    storageKey // Expose for debugging/testing
  };
};

/**
 * Utility function to clear all confirmation data for an extra (useful for logout)
 */
export const clearExtraConfirmations = (extraId: number) => {
  try {
    localStorage.removeItem(`profile_confirmation_checkbox_${extraId}`);
    localStorage.removeItem(`payroll_confirmation_checkbox_${extraId}`);
  } catch (error) {
    console.warn('Failed to clear extra confirmations from localStorage:', error);
  }
};

/**
 * Utility function to clear all confirmations (useful for complete logout/reset)
 */
export const clearAllConfirmations = () => {
  try {
    // Get all localStorage keys and remove confirmation-related ones
    const keys = Object.keys(localStorage);
    const confirmationKeys = keys.filter(key => 
      key.includes('_confirmation_checkbox')
    );
    
    confirmationKeys.forEach(key => {
      localStorage.removeItem(key);
    });
  } catch (error) {
    console.warn('Failed to clear all confirmations from localStorage:', error);
  }
};